import { DragEvent, FC, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import AddIcon from '@mui/icons-material/Add';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import VideocamIcon from '@mui/icons-material/Videocam';
import { Box, Stack, Tooltip, Typography } from '@mui/material';

import { FilePointerWithMeta, PostgresFilePointer, S3FilePointer } from '@/api/data-contracts';
import { INLINE_THUMBNAIL_LIMIT } from '@/features/media/lib/constants';
import ImagePdfModal from '@/features/media/ui/ImagesPdfModal';
import {
  ArtworkThumbnail,
  MoreArtworkButton,
} from '@/features/media/ui/TableCellFilesPreview/TableCellFilesPreview.styles';
import useUploadedFilesStore from '@/shared/api/uploadedFiles';
import { filesTooLargeMessage } from '@/shared/lib/files';
import { useSnackbar } from '@/shared/lib/hooks/useAppSnackbar';
import { ImageResource, useResourceImage } from '@/shared/lib/hooks/useResourceImage';
import { AttachmentOperationGroup } from '@/shared/ui/ImagesModalVersioning';

import {
  DragContainer,
  PdfBadge,
  PdfThumbnailContainer,
  UploadButtonContainer,
} from './ServiceTaskImagesPreview.styles';

const MAX_FILE_SIZE = 30 * 1024 * 1024; // 30MB

const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.heic', '.bmp'];
const VIDEO_EXTENSIONS = ['.mp4', '.webm', '.ogg', '.mov', '.avi'];
const DISPLAYABLE_EXTENSIONS = [...IMAGE_EXTENSIONS, ...VIDEO_EXTENSIONS, '.pdf'];

const isDisplayableFileName = (name: string): boolean =>
  DISPLAYABLE_EXTENSIONS.some((ext) => name.toLowerCase().endsWith(ext));

const extractFilePointer = (
  file: PostgresFilePointer | S3FilePointer | FilePointerWithMeta,
): PostgresFilePointer | S3FilePointer => {
  if ('file' in file) {
    return file.file;
  }
  return file;
};

interface ThumbnailItemProps {
  resource: ImageResource;
  fileName: string;
  onClick: () => void;
  testId?: string;
}

const ThumbnailItemImpl: FC<ThumbnailItemProps> = ({ resource, fileName, onClick, testId }) => {
  const [previewRequested, setPreviewRequested] = useState(false);
  const { url: thumbUrl } = useResourceImage(resource, { size: { tier: 'thumb' } });
  const { url: previewUrl } = useResourceImage(resource, {
    size: { tier: 'preview' },
    enabled: previewRequested,
  });

  const tooltipSrc = previewUrl ?? thumbUrl;

  return (
    <Tooltip
      placement="top"
      enterDelay={150}
      onOpen={() => setPreviewRequested(true)}
      title={
        tooltipSrc ? (
          <Box
            component="img"
            src={tooltipSrc}
            alt={fileName}
            sx={{ width: '200px', height: 'auto' }}
          />
        ) : null
      }
    >
      <ArtworkThumbnail
        data-testid={testId}
        src={thumbUrl ?? undefined}
        alt={fileName}
        onClick={onClick}
      />
    </Tooltip>
  );
};

const ThumbnailItem = memo(ThumbnailItemImpl);

interface Props {
  files?: (PostgresFilePointer | S3FilePointer | FilePointerWithMeta)[];
  resourceId: string;
  buildResource: (documentId: string) => ImageResource;
  onUpload?: (files: File[], operationId?: string) => void;
  onDelete?: (objectId: string) => void;
  isInternalOrder?: boolean;
  operationGroups?: AttachmentOperationGroup[];
  initialExpandedOperationId?: string;
}

const ImagesPdfFilesPreview: FC<Props> = ({
  files = [],
  resourceId,
  buildResource,
  onUpload,
  onDelete,
  isInternalOrder = true,
  operationGroups,
  initialExpandedOperationId,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const pendingOpen = useUploadedFilesStore((s) => s.pendingOpen);
  const clearPendingOpen = useUploadedFilesStore((s) => s.clearPendingOpen);
  const activeContextKey = useUploadedFilesStore((s) => s.activeContextKey);
  const registerOpenContext = useUploadedFilesStore((s) => s.registerOpenContext);
  const unregisterOpenContext = useUploadedFilesStore((s) => s.unregisterOpenContext);
  const setUploadContext = useUploadedFilesStore((s) => s.setUploadContext);
  const [modalOpen, setModalOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [initialSelectedObjectId, setInitialSelectedObjectId] = useState<string | undefined>(
    undefined,
  );

  useEffect(() => {
    if (!pendingOpen || pendingOpen.contextKey !== resourceId) return;

    if (pendingOpen.attachmentId) {
      setInitialSelectedObjectId(pendingOpen.attachmentId);
      setModalOpen(true);
      registerOpenContext(resourceId);
      clearPendingOpen();
      return;
    }

    const hasFile = files.some((f) => {
      const fp = extractFilePointer(f);
      return fp.name === pendingOpen.fileName;
    });
    if (!hasFile) return;

    setModalOpen(true);
    registerOpenContext(resourceId);
    clearPendingOpen();
  }, [pendingOpen, files, resourceId, clearPendingOpen, registerOpenContext]);

  useEffect(() => {
    if (activeContextKey && activeContextKey !== resourceId && modalOpen) {
      setModalOpen(false);
      unregisterOpenContext(resourceId);
    }
  }, [activeContextKey, resourceId, modalOpen, unregisterOpenContext]);

  const pdfFiles = useMemo(
    () => files.filter((f) => extractFilePointer(f).name?.toLowerCase().endsWith('.pdf')),
    [files],
  );

  const videoFiles = useMemo(
    () =>
      files.filter((f) => {
        const name = extractFilePointer(f).name?.toLowerCase() ?? '';
        return VIDEO_EXTENSIONS.some((ext) => name.endsWith(ext));
      }),
    [files],
  );

  const imageFiles = useMemo(
    () =>
      files.filter((f) => {
        const name = extractFilePointer(f).name?.toLowerCase() ?? '';
        return IMAGE_EXTENSIONS.some((ext) => name.endsWith(ext));
      }),
    [files],
  );

  const otherFiles = useMemo(
    () => files.filter((f) => !isDisplayableFileName(extractFilePointer(f).name ?? '')),
    [files],
  );

  const onThumbnailClick = () => {
    setModalOpen(true);
    registerOpenContext(resourceId);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (isInternalOrder && !isDragging) setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0 && onUpload) {
      const all = Array.from(e.dataTransfer.files);
      const unsupported = all.filter((f) => !isDisplayableFileName(f.name));
      const supported = all.filter((f) => isDisplayableFileName(f.name));
      const invalid = supported.filter((f) => f.size > MAX_FILE_SIZE);
      const valid = supported.filter((f) => f.size <= MAX_FILE_SIZE);

      if (unsupported.length) {
        enqueueSnackbar(
          t('errors.file_invalid_type', { types: DISPLAYABLE_EXTENSIONS.join(', ') }),
          {
            variant: 'warning',
          },
        );
      }

      if (invalid.length) {
        enqueueSnackbar(
          filesTooLargeMessage(
            t,
            invalid.map((f) => f.name),
            MAX_FILE_SIZE,
          ),
          { variant: 'warning' },
        );
      }

      if (valid.length) {
        valid.forEach((f) => setUploadContext(f.name, resourceId));
        onUpload(valid);
      }
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const emptyCallback = useCallback(() => {}, []);

  const handleModalClose = useCallback(() => {
    setModalOpen(false);
    setInitialSelectedObjectId(undefined);
    unregisterOpenContext(resourceId);
  }, [unregisterOpenContext, resourceId]);

  const handleModalUpload = useCallback(
    (uploadedFiles: File[], operationId?: string) => {
      uploadedFiles.forEach((f) => setUploadContext(f.name, resourceId));
      onUpload?.(uploadedFiles, operationId);
    },
    [setUploadContext, resourceId, onUpload],
  );

  const pdfTooltipTitle =
    pdfFiles.length === 1 ? extractFilePointer(pdfFiles[0]).name : `${pdfFiles.length} PDF files`;

  return (
    <>
      <DragContainer
        data-testid="simulation-files-preview"
        direction="row"
        alignItems="center"
        isDragging={isDragging}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {files?.length ? (
          <Stack direction="row" alignItems="center" ml="0.125rem">
            {imageFiles.slice(0, INLINE_THUMBNAIL_LIMIT).map((file, key) => {
              const fp = extractFilePointer(file);
              const objectId = fp.objectId;
              if (!objectId) return null;
              return (
                <ThumbnailItem
                  key={objectId}
                  resource={buildResource(objectId)}
                  fileName={fp.name ?? 'image'}
                  onClick={onThumbnailClick}
                  testId={`simulation-thumbnail-${key}`}
                />
              );
            })}

            {imageFiles.length > INLINE_THUMBNAIL_LIMIT && (
              <MoreArtworkButton data-testid="simulation-more-images" onClick={onThumbnailClick}>
                +{imageFiles.length - INLINE_THUMBNAIL_LIMIT}
              </MoreArtworkButton>
            )}

            {pdfFiles.length > 0 && (
              <Tooltip title={pdfTooltipTitle} placement="top">
                <PdfThumbnailContainer
                  data-testid="simulation-pdf-button"
                  onClick={onThumbnailClick}
                >
                  <PdfBadge badgeContent={pdfFiles.length} color="primary">
                    <PictureAsPdfIcon sx={{ color: '#c2c3ce', fontSize: '1.2rem' }} />
                  </PdfBadge>
                </PdfThumbnailContainer>
              </Tooltip>
            )}

            {videoFiles.length > 0 && (
              <Tooltip
                title={
                  videoFiles.length === 1
                    ? extractFilePointer(videoFiles[0]).name
                    : `${videoFiles.length} video files`
                }
                placement="top"
              >
                <PdfThumbnailContainer
                  data-testid="simulation-video-button"
                  onClick={onThumbnailClick}
                >
                  <PdfBadge badgeContent={videoFiles.length} color="primary">
                    <VideocamIcon sx={{ color: '#c2c3ce', fontSize: '1.2rem' }} />
                  </PdfBadge>
                </PdfThumbnailContainer>
              </Tooltip>
            )}

            {otherFiles.length > 0 && (
              <Tooltip
                title={otherFiles.map((f) => extractFilePointer(f).name).join(', ')}
                placement="top"
              >
                <PdfThumbnailContainer
                  data-testid="simulation-other-files-button"
                  onClick={onThumbnailClick}
                >
                  <PdfBadge badgeContent={otherFiles.length} color="primary">
                    <InsertDriveFileIcon sx={{ color: '#c2c3ce', fontSize: '1.2rem' }} />
                  </PdfBadge>
                </PdfThumbnailContainer>
              </Tooltip>
            )}
          </Stack>
        ) : (
          <Stack direction="row" alignItems="center" gap={1}>
            <Typography
              data-testid="simulation-not-uploaded"
              variant="body2"
              color="text.secondary"
              sx={{ whiteSpace: 'nowrap', fontWeight: 400 }}
            >
              {isDragging ? 'Drop here' : t('not_uploaded')}
            </Typography>

            {onUpload && (
              <Tooltip title="Drop files here or click to upload" placement="bottom" arrow>
                <UploadButtonContainer
                  data-testid="simulation-upload-button"
                  onClick={onThumbnailClick}
                >
                  <AddIcon fontSize="small" />
                </UploadButtonContainer>
              </Tooltip>
            )}
          </Stack>
        )}
      </DragContainer>

      {modalOpen && (
        <ImagePdfModal
          open={modalOpen}
          onClose={handleModalClose}
          resourceId={resourceId}
          files={files}
          buildResource={buildResource}
          onUpload={onUpload ? handleModalUpload : emptyCallback}
          onDelete={onDelete || emptyCallback}
          hasUploadOption={!!onUpload}
          hasDeleteOption={!!onDelete}
          initialSelectedObjectId={initialSelectedObjectId}
          operationGroups={operationGroups}
          initialExpandedOperationId={initialExpandedOperationId}
        />
      )}
    </>
  );
};

export default ImagesPdfFilesPreview;
